import type { LinearRGB, SRGB } from './types.js';

/**
 * sRGB electro-optical transfer function (IEC 61966-2-1).
 *
 * Extended to negative inputs by odd symmetry. Out-of-gamut values arise
 * constantly once you start applying calibration matrices, and silently
 * clamping them here would destroy information the caller may still need.
 * Clamp at the point you convert to 8-bit, not before.
 */
export function srgbTransferInverse(value: number): number {
  const sign = value < 0 ? -1 : 1;
  const a = Math.abs(value);
  return sign * (a <= 0.04045 ? a / 12.92 : Math.pow((a + 0.055) / 1.055, 2.4));
}

/** Inverse of {@link srgbTransferInverse}: linear light to gamma-encoded sRGB. */
export function srgbTransfer(value: number): number {
  const sign = value < 0 ? -1 : 1;
  const a = Math.abs(value);
  return sign * (a <= 0.0031308 ? a * 12.92 : 1.055 * Math.pow(a, 1 / 2.4) - 0.055);
}

export function srgbToLinear(c: SRGB): LinearRGB {
  return {
    r: srgbTransferInverse(c.r),
    g: srgbTransferInverse(c.g),
    b: srgbTransferInverse(c.b),
  };
}

export function linearToSrgb(c: LinearRGB): SRGB {
  return {
    r: srgbTransfer(c.r),
    g: srgbTransfer(c.g),
    b: srgbTransfer(c.b),
  };
}

/** Build an SRGB value from 8-bit channel values. */
export function srgbFrom8Bit(r: number, g: number, b: number): SRGB {
  return { r: r / 255, g: g / 255, b: b / 255 };
}

/** Quantise to 8-bit, clamping out-of-gamut components. */
export function srgbTo8Bit(c: SRGB): [number, number, number] {
  const q = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
  return [q(c.r), q(c.g), q(c.b)];
}

/** True when any component falls outside the displayable [0, 1] range. */
export function isOutOfGamut(c: SRGB, epsilon = 1e-6): boolean {
  return [c.r, c.g, c.b].some((v) => v < -epsilon || v > 1 + epsilon);
}

export function srgbToHex(c: SRGB): string {
  const [r, g, b] = srgbTo8Bit(c);
  return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
}

export function hexToSrgb(hex: string): SRGB {
  const h = hex.replace('#', '').trim();
  const full = h.length === 3 ? h.split('').map((ch) => ch + ch).join('') : h;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) {
    throw new Error(`Not a hex colour: ${hex}`);
  }
  return srgbFrom8Bit(
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  );
}
